import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Search,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Users,
    Calendar,
    Phone,
    Mail,
    MapPin,
    User,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    X,
    Building2,
    Loader2,
    ExternalLink,
    Video,
    MessageSquare,
    GraduationCap,
    Award,
    CalendarDays,
    RotateCcw,
} from 'lucide-react';
import { showToast, ToastManager } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

// ==================== INTERFACES ====================

interface CounsellorData {
    userId: number;
    uuid: string;
    userName: string;
    userEmail: string;
    phone: string;
    client_id: number;
    profile_pic: string;
    user_slug: string;
    designation: string;
    description: string;
    counselor_type: string;
    expertise: string;
    school: {
        slate_id: number;
        slate_name: string;
        slate_slug: string;
        city: string;
        area: string;
    } | null;
}

interface BookingData {
    booking_id: number;
    uuid: string;
    parent_name: string;
    parent_email: string;
    parent_phone: string | null;
    student_grade: string | null;
    message: string | null;
    booking_date: string;
    booking_slot_time: string;
    meeting_url: string | null;
    status: string;
    created_at: string;
}

interface Pagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

interface DateRange {
    startDate: string;
    endDate: string;
}

type TabId = 'directory' | 'booking';

const TABS = [
    { id: 'directory' as TabId, label: 'Counsellors', icon: <Users size={18} /> },
    { id: 'booking' as TabId, label: 'Booking Management', icon: <Calendar size={18} /> },
];

const API_BASE_URL = 'https://crm-api.yellowslate.com/api/public/counsellors';

const CounsellorsAdmin: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const schoolId = id ? parseInt(id) : null;

    const [activeTab, setActiveTab] = useState<TabId>('directory');

    // Counsellor state
    const [counsellors, setCounsellors] = useState<CounsellorData[]>([]);
    const [allCounsellorsData, setAllCounsellorsData] = useState<CounsellorData[]>([]);
    const [isLoadingCounsellors, setIsLoadingCounsellors] = useState(true);
    const [counsellorSearchTerm, setCounsellorSearchTerm] = useState('');
    const [counsellorTypeFilter, _setCounsellorTypeFilter] = useState<string>('');
    const [counsellorPagination, setCounsellorPagination] = useState<Pagination>({
        currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 25,
    });

    // Booking state
    const [allBookings, setAllBookings] = useState<Array<BookingData & { counsellor: CounsellorData }>>([]);
    const [filteredBookings, setFilteredBookings] = useState<Array<BookingData & { counsellor: CounsellorData }>>([]);
    const [isLoadingAllBookings, setIsLoadingAllBookings] = useState(false);
    const [bookingSearchTerm, setBookingSearchTerm] = useState('');
    const [bookingStatusFilter, setBookingStatusFilter] = useState<string>('');
    const [bookingPagination, setBookingPagination] = useState<Pagination>({
        currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 25,
    });

    // Date range filter state
    const [dateRange, setDateRange] = useState<DateRange>({ startDate: '', endDate: '' });

    // Slider state
    const [selectedCounsellor, setSelectedCounsellor] = useState<CounsellorData | null>(null);
    const [selectedBooking, setSelectedBooking] = useState<(BookingData & { counsellor?: CounsellorData }) | null>(null);
    const [isCounsellorSliderOpen, setIsCounsellorSliderOpen] = useState(false);
    const [isBookingSliderOpen, setIsBookingSliderOpen] = useState(false);
    const [isLoadingCounsellorBookings, setIsLoadingCounsellorBookings] = useState(false);
    const [counsellorBookings, setCounsellorBookings] = useState<BookingData[]>([]);

    // Sort state
    const [counsellorSortColumn, setCounsellorSortColumn] = useState('userName');
    const [counsellorSortDirection, setCounsellorSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const [bookingSortColumn, setBookingSortColumn] = useState('booking_date');
    const [bookingSortDirection, setBookingSortDirection] = useState<'ASC' | 'DESC'>('DESC');

    // ==================== DATE RANGE HELPERS ====================

    const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
        setDateRange(prev => ({ ...prev, [field]: value }));
    };

    const clearDateFilter = () => {
        setDateRange({ startDate: '', endDate: '' });
    };

    const isDateInRange = (dateString: string): boolean => {
        if (!dateRange.startDate && !dateRange.endDate) return true;
        if (!dateString) return true;

        const date = new Date(dateString);
        date.setHours(0, 0, 0, 0);

        if (dateRange.startDate && dateRange.endDate) {
            const start = new Date(dateRange.startDate);
            const end = new Date(dateRange.endDate);
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            return date >= start && date <= end;
        }

        if (dateRange.startDate) {
            const start = new Date(dateRange.startDate);
            start.setHours(0, 0, 0, 0);
            return date >= start;
        }

        if (dateRange.endDate) {
            const end = new Date(dateRange.endDate);
            end.setHours(23, 59, 59, 999);
            return date <= end;
        }

        return true;
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        if (bookingSearchTerm) count++;
        if (bookingStatusFilter) count++;
        if (dateRange.startDate || dateRange.endDate) count++;
        return count;
    };

    const clearAllFilters = () => {
        setBookingSearchTerm('');
        setBookingStatusFilter('');
        clearDateFilter();
    };

    // ==================== API CALLS ====================

    const fetchCounsellors = async () => {
        try {
            setIsLoadingCounsellors(true);
            const url = schoolId ? `${API_BASE_URL}/school/${schoolId}` : API_BASE_URL;
            const response = await fetch(url);
            const result = await response.json();
            if (result?.status === 'success' && result?.data) {
                setAllCounsellorsData(result.data);
                applyFiltersAndPagination(result.data);
            } else {
                setAllCounsellorsData([]);
                setCounsellors([]);
            }
        } catch (error) {
            console.error('Error fetching counsellors:', error);
            showToast('Failed to fetch counsellor data', 'error');
            setAllCounsellorsData([]);
            setCounsellors([]);
        } finally {
            setIsLoadingCounsellors(false);
        }
    };

    const fetchCounsellorBookings = async (uuid: string) => {
        try {
            setIsLoadingCounsellorBookings(true);
            const response = await fetch(`${API_BASE_URL}/${uuid}/bookings`);
            const result = await response.json();
            if (result?.status === 'success' && result?.data) {
                setCounsellorBookings(result.data);
            } else {
                setCounsellorBookings([]);
            }
        } catch (error) {
            console.error('Error fetching bookings:', error);
            showToast('Failed to fetch booking data', 'error');
            setCounsellorBookings([]);
        } finally {
            setIsLoadingCounsellorBookings(false);
        }
    };

    const fetchAllBookings = async () => {
        try {
            setIsLoadingAllBookings(true);
            const allBookingsArray: Array<BookingData & { counsellor: CounsellorData }> = [];
            for (const counsellor of allCounsellorsData) {
                try {
                    const response = await fetch(`${API_BASE_URL}/${counsellor.uuid}/bookings`);
                    const result = await response.json();
                    if (result?.status === 'success' && result?.data) {
                        const bookingsWithCounsellor = result.data.map((booking: BookingData) => ({
                            ...booking,
                            counsellor: counsellor,
                        }));
                        allBookingsArray.push(...bookingsWithCounsellor);
                    }
                } catch (err) {
                    console.error(`Error fetching bookings for ${counsellor.userName}:`, err);
                }
            }
            allBookingsArray.sort((a, b) => new Date(b.booking_date).getTime() - new Date(a.booking_date).getTime());
            setAllBookings(allBookingsArray);
            applyBookingFiltersAndPagination(allBookingsArray);
        } catch (error) {
            console.error('Error fetching all bookings:', error);
            showToast('Failed to fetch bookings', 'error');
        } finally {
            setIsLoadingAllBookings(false);
        }
    };

    const applyFiltersAndPagination = (data: CounsellorData[], page: number = 1) => {
        let filtered = [...data];
        if (counsellorSearchTerm) {
            const search = counsellorSearchTerm.toLowerCase();
            filtered = filtered.filter(c =>
                c.userName?.toLowerCase().includes(search) ||
                c.userEmail?.toLowerCase().includes(search) ||
                c.phone?.includes(counsellorSearchTerm) ||
                c.school?.slate_name?.toLowerCase().includes(search)
            );
        }
        if (counsellorTypeFilter) {
            filtered = filtered.filter(c => c.counselor_type === counsellorTypeFilter);
        }
        filtered.sort((a, b) => {
            let aVal = '', bVal = '';
            switch (counsellorSortColumn) {
                case 'userName': aVal = a.userName || ''; bVal = b.userName || ''; break;
                case 'userEmail': aVal = a.userEmail || ''; bVal = b.userEmail || ''; break;
                case 'counselor_type': aVal = a.counselor_type || ''; bVal = b.counselor_type || ''; break;
                case 'school': aVal = a.school?.slate_name || ''; bVal = b.school?.slate_name || ''; break;
                default: aVal = a.userName || ''; bVal = b.userName || '';
            }
            return counsellorSortDirection === 'ASC' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / counsellorPagination.itemsPerPage) || 1;
        const start = (page - 1) * counsellorPagination.itemsPerPage;
        setCounsellors(filtered.slice(start, start + counsellorPagination.itemsPerPage));
        setCounsellorPagination(prev => ({ ...prev, currentPage: page, totalItems, totalPages }));
    };

    const applyBookingFiltersAndPagination = (data: Array<BookingData & { counsellor: CounsellorData }>, page: number = 1) => {
        let filtered = [...data];
        if (bookingSearchTerm) {
            const search = bookingSearchTerm.toLowerCase();
            filtered = filtered.filter(b =>
                b.parent_name?.toLowerCase().includes(search) ||
                b.parent_email?.toLowerCase().includes(search) ||
                b.counsellor?.userName?.toLowerCase().includes(search) ||
                b.counsellor?.school?.slate_name?.toLowerCase().includes(search)
            );
        }
        if (bookingStatusFilter) {
            filtered = filtered.filter(b => b.status === bookingStatusFilter);
        }
        // Apply date range filter
        if (dateRange.startDate || dateRange.endDate) {
            filtered = filtered.filter(b => isDateInRange(b.booking_date));
        }
        filtered.sort((a, b) => {
            let aVal = '', bVal = '';
            switch (bookingSortColumn) {
                case 'booking_date': aVal = a.booking_date || ''; bVal = b.booking_date || ''; break;
                case 'parent_name': aVal = a.parent_name || ''; bVal = b.parent_name || ''; break;
                case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
                case 'counsellor': aVal = a.counsellor?.userName || ''; bVal = b.counsellor?.userName || ''; break;
                default: aVal = a.booking_date || ''; bVal = b.booking_date || '';
            }
            if (bookingSortColumn === 'booking_date') {
                const dateA = new Date(aVal).getTime();
                const dateB = new Date(bVal).getTime();
                return bookingSortDirection === 'ASC' ? dateA - dateB : dateB - dateA;
            }
            return bookingSortDirection === 'ASC' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / bookingPagination.itemsPerPage) || 1;
        const start = (page - 1) * bookingPagination.itemsPerPage;
        setFilteredBookings(filtered.slice(start, start + bookingPagination.itemsPerPage));
        setBookingPagination(prev => ({ ...prev, currentPage: page, totalItems, totalPages }));
    };

    // ==================== EFFECTS ====================

    useEffect(() => {
        fetchCounsellors();
        setAllBookings([]);
        setFilteredBookings([]);
    }, [schoolId]);

    useEffect(() => {
        if (allCounsellorsData.length > 0) {
            applyFiltersAndPagination(allCounsellorsData, 1);
        }
    }, [counsellorSearchTerm, counsellorTypeFilter, counsellorSortColumn, counsellorSortDirection]);

    useEffect(() => {
        if (activeTab === 'booking' && allCounsellorsData.length > 0 && allBookings.length === 0) {
            fetchAllBookings();
        }
    }, [activeTab, allCounsellorsData]);

    useEffect(() => {
        if (allBookings.length > 0) {
            applyBookingFiltersAndPagination(allBookings, 1);
        }
    }, [bookingSearchTerm, bookingStatusFilter, bookingSortColumn, bookingSortDirection, dateRange]);

    // ==================== HANDLERS ====================

    const handleCounsellorPageChange = (page: number) => {
        if (page >= 1 && page <= counsellorPagination.totalPages) {
            applyFiltersAndPagination(allCounsellorsData, page);
        }
    };

    const handleBookingPageChange = (page: number) => {
        if (page >= 1 && page <= bookingPagination.totalPages) {
            applyBookingFiltersAndPagination(allBookings, page);
        }
    };

    const handleCounsellorSort = (column: string) => {
        if (counsellorSortColumn === column) {
            setCounsellorSortDirection(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setCounsellorSortColumn(column);
            setCounsellorSortDirection('ASC');
        }
    };

    const handleBookingSort = (column: string) => {
        if (bookingSortColumn === column) {
            setBookingSortDirection(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setBookingSortColumn(column);
            setBookingSortDirection('ASC');
        }
    };

    const handleCounsellorClick = async (counsellor: CounsellorData) => {
        setSelectedCounsellor(counsellor);
        setIsCounsellorSliderOpen(true);
        setCounsellorBookings([]);
        await fetchCounsellorBookings(counsellor.uuid);
    };

    const handleBookingClick = (booking: BookingData & { counsellor?: CounsellorData }) => {
        setSelectedBooking(booking);
        setIsBookingSliderOpen(true);
    };

    // ==================== HELPERS ====================

    const formatCounsellorType = (type: string) => {
        if (!type) return '-';
        return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
    };

    const getStatusBadge = (status: string) => {
        const styles: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
            confirmed: { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle size={12} /> },
            pending: { bg: 'bg-amber-100', text: 'text-amber-800', icon: <Clock size={12} /> },
            completed: { bg: 'bg-blue-100', text: 'text-blue-800', icon: <CheckCircle size={12} /> },
            cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: <XCircle size={12} /> },
        };
        const s = styles[status?.toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: <AlertCircle size={12} /> };
        return (
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${s.bg} ${s.text}`}>
                {s.icon}{status ? status.charAt(0).toUpperCase() + status.slice(1) : '-'}
            </span>
        );
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch { return dateString; }
    };

    const truncateText = (text: string, max: number = 30) => text ? (text.length > max ? text.substring(0, max) + '...' : text) : '-';

    const getProfileImage = (profilePic: string) => {
        if (!profilePic || profilePic === 'user.png') return null;
        return profilePic;
    };

    const getSchoolName = () => {
        if (allCounsellorsData.length > 0 && allCounsellorsData[0]?.school?.slate_name) {
            return allCounsellorsData[0].school.slate_name;
        }
        return schoolId ? `School ID: ${schoolId}` : 'All Schools';
    };

    // Get header info based on active tab
    const getHeaderInfo = () => {
        if (activeTab === 'directory') {
            return {
                title: 'Counsellor Management',
                description: schoolId ? getSchoolName() : 'Manage all counsellors and their bookings',
                icon: <Users className="h-5 w-5" />,
                count: counsellorPagination.totalItems
            };
        }
        return {
            title: 'Booking Management',
            description: schoolId ? `Bookings for ${getSchoolName()}` : 'View and manage all counsellor bookings',
            icon: <Calendar className="h-5 w-5" />,
            count: bookingPagination.totalItems
        };
    };

    const headerInfo = getHeaderInfo();

    return (
        <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
            <ToastManager />

            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3" style={{ flex: '0 0 40%' }}>
                        <div className="p-2 bg-brandColor rounded-lg text-white">{headerInfo.icon}</div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{headerInfo.title}</h1>
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                                    {headerInfo.count}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{headerInfo.description}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 justify-end" style={{ flex: '0 0 60%' }}>
                        {activeTab === 'directory' ? (
                            <div className="relative" style={{ width: '300px' }}>
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search counsellors..."
                                    value={counsellorSearchTerm}
                                    onChange={(e) => setCounsellorSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-700 dark:text-white transition-all"
                                />
                            </div>
                        ) : (
                            <>
                                <div className="relative" style={{ width: '300px' }}>
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                                    <input
                                        type="text"
                                        placeholder="Search bookings..."
                                        value={bookingSearchTerm}
                                        onChange={(e) => setBookingSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-700 dark:text-white transition-all"
                                    />
                                </div>
                                <select
                                    value={bookingStatusFilter}
                                    onChange={(e) => setBookingStatusFilter(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-700 dark:text-white transition-all"
                                >
                                    <option value="">All Status</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="pending">Pending</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Date Range Filter - Only for Booking tab */}
            {activeTab === 'booking' && (
                <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-3">
                    <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            <CalendarDays size={18} className="text-brandColor" />
                            <span>Filter by Date:</span>
                        </div>

                        {/* Date Range Inputs */}
                        <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">From</label>
                                <input
                                    type="date"
                                    value={dateRange.startDate}
                                    onChange={(e) => handleDateChange('startDate', e.target.value)}
                                    className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-600 dark:text-white text-sm transition-all"
                                />
                            </div>
                            <div className="w-4 h-px bg-gray-300 dark:bg-gray-500"></div>
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">To</label>
                                <input
                                    type="date"
                                    value={dateRange.endDate}
                                    onChange={(e) => handleDateChange('endDate', e.target.value)}
                                    min={dateRange.startDate}
                                    className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-600 dark:text-white text-sm transition-all"
                                />
                            </div>
                            {(dateRange.startDate || dateRange.endDate) && (
                                <button
                                    onClick={clearDateFilter}
                                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                                    title="Clear date filter"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        {/* Active Date Range Badge */}
                        {(dateRange.startDate || dateRange.endDate) && (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-brandColor/10 text-brandColor rounded-full text-sm font-medium">
                                <Calendar size={14} />
                                <span>
                                    {dateRange.startDate ? formatDate(dateRange.startDate) : 'Start'}
                                    {' → '}
                                    {dateRange.endDate ? formatDate(dateRange.endDate) : 'End'}
                                </span>
                            </div>
                        )}

                        {/* Clear All Filters */}
                        {getActiveFiltersCount() > 0 && (
                            <button
                                onClick={clearAllFilters}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors ml-auto"
                            >
                                <RotateCcw size={14} />
                                Clear All Filters ({getActiveFiltersCount()})
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8">
                <div className="flex gap-1">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'text-brandColor border-brandColor bg-brandColor/5' : 'text-gray-500 border-transparent hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                        >
                            {tab.icon}{tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Directory Table */}
            {activeTab === 'directory' && (
                <>
                    <div className="flex-1 overflow-auto px-4 mt-4">
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                            <table className="min-w-full table-auto border-collapse">
                                <thead className="bg-brandColor text-white sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">User ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-white/10" onClick={() => handleCounsellorSort('userName')}>
                                            Name {counsellorSortColumn === 'userName' && (counsellorSortDirection === 'ASC' ? '↑' : '↓')}
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-white/10" onClick={() => handleCounsellorSort('userEmail')}>
                                            Email {counsellorSortColumn === 'userEmail' && (counsellorSortDirection === 'ASC' ? '↑' : '↓')}
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Phone</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Designation</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-white/10" onClick={() => handleCounsellorSort('counselor_type')}>
                                            Type {counsellorSortColumn === 'counselor_type' && (counsellorSortDirection === 'ASC' ? '↑' : '↓')}
                                        </th>
                                        {!schoolId && (
                                            <>
                                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-white/10" onClick={() => handleCounsellorSort('school')}>
                                                    School {counsellorSortColumn === 'school' && (counsellorSortDirection === 'ASC' ? '↑' : '↓')}
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Location</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {isLoadingCounsellors ? (
                                        <tr><td colSpan={schoolId ? 6 : 8} className="px-4 py-8 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading counsellors...</td></tr>
                                    ) : counsellors.length === 0 ? (
                                        <tr><td colSpan={schoolId ? 6 : 8} className="px-4 py-8 text-center text-gray-500"><Users size={40} className="mx-auto text-gray-300 mb-2" />No counsellors found{schoolId ? ' for this school' : ''}</td></tr>
                                    ) : (
                                        counsellors.map((c, i) => (
                                            <tr key={c.userId} onClick={() => handleCounsellorClick(c)} className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${i % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}`}>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{c.userId}</td>
                                                <td className="px-4 py-3 text-sm">
                                                    <div className="flex items-center gap-3">
                                                        {getProfileImage(c.profile_pic) ? (
                                                            <img src={c.profile_pic} alt={c.userName} className="w-9 h-9 rounded-full object-cover" />
                                                        ) : (
                                                            <div className="w-9 h-9 bg-brandColor/10 rounded-full flex items-center justify-center">
                                                                <User size={18} className="text-brandColor" />
                                                            </div>
                                                        )}
                                                        <span className="font-medium text-gray-800 dark:text-gray-200">{c.userName}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{truncateText(c.userEmail, 25)}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{c.phone || '-'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{truncateText(c.designation, 20)}</td>
                                                <td className="px-4 py-3 text-sm">
                                                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                                        {formatCounsellorType(c.counselor_type)}
                                                    </span>
                                                </td>
                                                {!schoolId && (
                                                    <>
                                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{truncateText(c.school?.slate_name || '-', 25)}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                            {c.school ? (
                                                                <span className="flex items-center gap-1">
                                                                    <MapPin size={14} className="text-gray-400" />
                                                                    {c.school.city}{c.school.area ? `, ${c.school.area}` : ''}
                                                                </span>
                                                            ) : '-'}
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Showing {counsellorPagination.totalItems === 0 ? 0 : ((counsellorPagination.currentPage - 1) * counsellorPagination.itemsPerPage) + 1} to {Math.min(counsellorPagination.currentPage * counsellorPagination.itemsPerPage, counsellorPagination.totalItems)} of {counsellorPagination.totalItems}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleCounsellorPageChange(1)} disabled={counsellorPagination.currentPage === 1}><ChevronsLeft size={16} /></Button>
                            <Button variant="outline" size="sm" onClick={() => handleCounsellorPageChange(counsellorPagination.currentPage - 1)} disabled={counsellorPagination.currentPage === 1}><ChevronLeft size={16} /></Button>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Page {counsellorPagination.currentPage} of {counsellorPagination.totalPages}</span>
                            <Button variant="outline" size="sm" onClick={() => handleCounsellorPageChange(counsellorPagination.currentPage + 1)} disabled={counsellorPagination.currentPage >= counsellorPagination.totalPages}><ChevronRight size={16} /></Button>
                            <Button variant="outline" size="sm" onClick={() => handleCounsellorPageChange(counsellorPagination.totalPages)} disabled={counsellorPagination.currentPage >= counsellorPagination.totalPages}><ChevronsRight size={16} /></Button>
                        </div>
                    </div>
                </>
            )}

            {/* Booking Management Table */}
            {activeTab === 'booking' && (
                <>
                    <div className="flex-1 overflow-auto px-4 mt-4">
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                            <table className="min-w-full table-auto border-collapse">
                                <thead className="bg-brandColor text-white sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Booking ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-white/10" onClick={() => handleBookingSort('counsellor')}>
                                            Counsellor {bookingSortColumn === 'counsellor' && (bookingSortDirection === 'ASC' ? '↑' : '↓')}
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-white/10" onClick={() => handleBookingSort('parent_name')}>
                                            Parent Name {bookingSortColumn === 'parent_name' && (bookingSortDirection === 'ASC' ? '↑' : '↓')}
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Parent Email</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-white/10" onClick={() => handleBookingSort('booking_date')}>
                                            Date {bookingSortColumn === 'booking_date' && (bookingSortDirection === 'ASC' ? '↑' : '↓')}
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Time</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-white/10" onClick={() => handleBookingSort('status')}>
                                            Status {bookingSortColumn === 'status' && (bookingSortDirection === 'ASC' ? '↑' : '↓')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {isLoadingAllBookings ? (
                                        <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading bookings...</td></tr>
                                    ) : filteredBookings.length === 0 ? (
                                        <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500"><Calendar size={40} className="mx-auto text-gray-300 mb-2" />No bookings found</td></tr>
                                    ) : (
                                        filteredBookings.map((b, i) => (
                                            <tr key={b.booking_id} onClick={() => handleBookingClick(b)} className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${i % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}`}>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">#{b.booking_id}</td>
                                                <td className="px-4 py-3 text-sm">
                                                    <div className="flex items-center gap-2">
                                                        {getProfileImage(b.counsellor?.profile_pic || '') ? (
                                                            <img src={b.counsellor?.profile_pic} alt={b.counsellor?.userName} className="w-8 h-8 rounded-full object-cover" />
                                                        ) : (
                                                            <div className="w-8 h-8 bg-brandColor/10 rounded-full flex items-center justify-center">
                                                                <User size={14} className="text-brandColor" />
                                                            </div>
                                                        )}
                                                        <span className="font-medium text-gray-800 dark:text-gray-200">{b.counsellor?.userName || '-'}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200 font-medium">{b.parent_name || '-'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{truncateText(b.parent_email, 25)}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar size={14} className="text-gray-400" />
                                                        {formatDate(b.booking_date)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={14} className="text-gray-400" />
                                                        {b.booking_slot_time || '-'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm">{getStatusBadge(b.status)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Showing {bookingPagination.totalItems === 0 ? 0 : ((bookingPagination.currentPage - 1) * bookingPagination.itemsPerPage) + 1} to {Math.min(bookingPagination.currentPage * bookingPagination.itemsPerPage, bookingPagination.totalItems)} of {bookingPagination.totalItems}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleBookingPageChange(1)} disabled={bookingPagination.currentPage === 1}><ChevronsLeft size={16} /></Button>
                            <Button variant="outline" size="sm" onClick={() => handleBookingPageChange(bookingPagination.currentPage - 1)} disabled={bookingPagination.currentPage === 1}><ChevronLeft size={16} /></Button>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Page {bookingPagination.currentPage} of {bookingPagination.totalPages}</span>
                            <Button variant="outline" size="sm" onClick={() => handleBookingPageChange(bookingPagination.currentPage + 1)} disabled={bookingPagination.currentPage >= bookingPagination.totalPages}><ChevronRight size={16} /></Button>
                            <Button variant="outline" size="sm" onClick={() => handleBookingPageChange(bookingPagination.totalPages)} disabled={bookingPagination.currentPage >= bookingPagination.totalPages}><ChevronsRight size={16} /></Button>
                        </div>
                    </div>
                </>
            )}

            {/* Counsellor Detail Slider */}
            {isCounsellorSliderOpen && selectedCounsellor && (
                <div className="fixed inset-0 z-[9999]">
                    <div onClick={() => { setIsCounsellorSliderOpen(false); setSelectedCounsellor(null); setCounsellorBookings([]); }} className="fixed inset-0 bg-black/70 cursor-pointer" />
                    <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
                        <div className="flex items-center justify-between p-6 bg-brandColor flex-shrink-0">
                            <div className="flex items-center gap-3">
                                {getProfileImage(selectedCounsellor.profile_pic) ? (
                                    <img src={selectedCounsellor.profile_pic} alt={selectedCounsellor.userName} className="w-12 h-12 rounded-full object-cover border-2 border-white/30" />
                                ) : (
                                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                                        <User className="w-6 h-6 text-white" />
                                    </div>
                                )}
                                <div>
                                    <h3 className="text-lg font-semibold text-white">{selectedCounsellor.userName}</h3>
                                    <p className="text-white/80 text-sm">{selectedCounsellor.designation || 'Counsellor'}</p>
                                </div>
                            </div>
                            <button onClick={() => { setIsCounsellorSliderOpen(false); setSelectedCounsellor(null); setCounsellorBookings([]); }} className="text-white/80 hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <div className="flex items-start gap-4">
                                    {getProfileImage(selectedCounsellor.profile_pic) ? (
                                        <img src={selectedCounsellor.profile_pic} alt={selectedCounsellor.userName} className="w-20 h-20 rounded-full object-cover" />
                                    ) : (
                                        <div className="w-20 h-20 bg-brandColor/10 rounded-full flex items-center justify-center">
                                            <User size={40} className="text-brandColor" />
                                        </div>
                                    )}
                                    <div className="flex-1">
                                        <h4 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedCounsellor.userName}</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{selectedCounsellor.designation}</p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                                {formatCounsellorType(selectedCounsellor.counselor_type)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {selectedCounsellor.description && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 leading-relaxed">{selectedCounsellor.description}</p>
                                )}
                            </div>
                            {selectedCounsellor.expertise && (
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase flex items-center gap-2">
                                        <Award size={16} className="text-brandColor" />Expertise
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedCounsellor.expertise.split(',').map((exp, i) => (
                                            <span key={i} className="px-3 py-1.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">{exp.trim()}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="space-y-3">
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase">Contact Information</h4>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Mail size={20} className="text-blue-600" /></div>
                                        <div><p className="text-xs text-gray-500 uppercase">Email</p><p className="text-sm font-medium text-gray-900 dark:text-white">{selectedCounsellor.userEmail}</p></div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><Phone size={20} className="text-green-600" /></div>
                                        <div><p className="text-xs text-gray-500 uppercase">Phone</p><p className="text-sm font-medium text-gray-900 dark:text-white">{selectedCounsellor.phone}</p></div>
                                    </div>
                                </div>
                            </div>
                            {selectedCounsellor.school && (
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase">School Information</h4>
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 bg-brandColor/10 rounded-lg"><Building2 className="w-5 h-5 text-brandColor" /></div>
                                            <div>
                                                <h5 className="font-semibold text-gray-900 dark:text-white">{selectedCounsellor.school.slate_name}</h5>
                                                <div className="flex items-center gap-2 mt-1 text-sm text-gray-600 dark:text-gray-400">
                                                    <MapPin size={14} />
                                                    {selectedCounsellor.school.city}{selectedCounsellor.school.area ? `, ${selectedCounsellor.school.area}` : ''}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="space-y-3 border-t dark:border-gray-700 pt-4">
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase flex items-center gap-2">
                                    <Calendar size={16} className="text-brandColor" />Bookings
                                    {counsellorBookings.length > 0 && <span className="px-2 py-0.5 text-xs rounded-full bg-brandColor text-white">{counsellorBookings.length}</span>}
                                </h4>
                                {isLoadingCounsellorBookings ? (
                                    <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-brandColor" /><span className="ml-2 text-gray-500">Loading bookings...</span></div>
                                ) : counsellorBookings.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500"><Calendar size={40} className="mx-auto text-gray-300 mb-2" />No bookings found</div>
                                ) : (
                                    <div className="space-y-3 max-h-80 overflow-y-auto">
                                        {counsellorBookings.map((b) => (
                                            <div key={b.booking_id} onClick={() => handleBookingClick({ ...b, counsellor: selectedCounsellor })} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <User size={14} className="text-gray-400" />
                                                            <span className="font-medium text-gray-900 dark:text-white">{b.parent_name}</span>
                                                        </div>
                                                        <p className="text-xs text-gray-500 mt-1">{b.parent_email}</p>
                                                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                                                            <span className="flex items-center gap-1"><Calendar size={12} />{formatDate(b.booking_date)}</span>
                                                            <span className="flex items-center gap-1"><Clock size={12} />{b.booking_slot_time}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-end gap-2">
                                                        {getStatusBadge(b.status)}
                                                        {b.meeting_url && <Video size={16} className="text-blue-500" />}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0">
                            <button onClick={() => { setIsCounsellorSliderOpen(false); setSelectedCounsellor(null); setCounsellorBookings([]); }} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Booking Detail Slider */}
            {isBookingSliderOpen && selectedBooking && (
                <div className="fixed inset-0 z-[9999]">
                    <div onClick={() => { setIsBookingSliderOpen(false); setSelectedBooking(null); }} className="fixed inset-0 bg-black/70 cursor-pointer" />
                    <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
                        <div className="flex items-center justify-between p-6 bg-brandColor flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"><Calendar className="w-5 h-5 text-white" /></div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white">Booking Details</h3>
                                    <p className="text-white/80 text-sm">Booking #{selectedBooking.booking_id}</p>
                                </div>
                            </div>
                            <button onClick={() => { setIsBookingSliderOpen(false); setSelectedBooking(null); }} className="text-white/80 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            <div className="flex items-center justify-between pb-4 border-b dark:border-gray-700">
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedBooking.parent_name}</h4>
                                    <p className="text-sm text-gray-500 mt-1">{selectedBooking.parent_email}</p>
                                </div>
                                {getStatusBadge(selectedBooking.status)}
                            </div>
                            <div className="space-y-3">
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase">Booking Information</h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                        <div className="flex items-center gap-2 mb-1"><Calendar size={16} className="text-blue-600" /><p className="text-xs text-gray-500 uppercase">Date</p></div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(selectedBooking.booking_date)}</p>
                                    </div>
                                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                        <div className="flex items-center gap-2 mb-1"><Clock size={16} className="text-green-600" /><p className="text-xs text-gray-500 uppercase">Time</p></div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedBooking.booking_slot_time}</p>
                                    </div>
                                </div>
                            </div>
                            {selectedBooking.meeting_url && (
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase flex items-center gap-2">
                                        <Video size={16} className="text-brandColor" />Meeting Link
                                    </h4>
                                    <a href={selectedBooking.meeting_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
                                        <ExternalLink size={16} />
                                        <span className="text-sm font-medium truncate">{selectedBooking.meeting_url}</span>
                                    </a>
                                </div>
                            )}
                            <div className="space-y-3">
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase">Parent Information</h4>
                                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
                                    <div className="flex items-center gap-3"><User size={18} className="text-gray-400" /><div><p className="text-xs text-gray-500">Name</p><p className="text-sm font-medium text-gray-900 dark:text-white">{selectedBooking.parent_name}</p></div></div>
                                    <div className="flex items-center gap-3"><Mail size={18} className="text-gray-400" /><div><p className="text-xs text-gray-500">Email</p><p className="text-sm font-medium text-gray-900 dark:text-white">{selectedBooking.parent_email}</p></div></div>
                                    {selectedBooking.parent_phone && (
                                        <div className="flex items-center gap-3"><Phone size={18} className="text-gray-400" /><div><p className="text-xs text-gray-500">Phone</p><p className="text-sm font-medium text-gray-900 dark:text-white">{selectedBooking.parent_phone}</p></div></div>
                                    )}
                                    {selectedBooking.student_grade && (
                                        <div className="flex items-center gap-3"><GraduationCap size={18} className="text-gray-400" /><div><p className="text-xs text-gray-500">Student Grade</p><p className="text-sm font-medium text-gray-900 dark:text-white">{selectedBooking.student_grade}</p></div></div>
                                    )}
                                </div>
                            </div>
                            {selectedBooking.counsellor && (
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase">Counsellor</h4>
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        {getProfileImage(selectedBooking.counsellor.profile_pic) ? (
                                            <img src={selectedBooking.counsellor.profile_pic} alt={selectedBooking.counsellor.userName} className="w-10 h-10 rounded-full object-cover" />
                                        ) : (
                                            <div className="w-10 h-10 bg-brandColor/10 rounded-full flex items-center justify-center"><User size={18} className="text-brandColor" /></div>
                                        )}
                                        <div>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedBooking.counsellor.userName}</p>
                                            <p className="text-xs text-gray-500">{selectedBooking.counsellor.designation}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {selectedBooking.message && (
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase flex items-center gap-2">
                                        <MessageSquare size={16} className="text-brandColor" />Message
                                    </h4>
                                    <p className="text-sm text-gray-700 dark:text-gray-300 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">{selectedBooking.message}</p>
                                </div>
                            )}
                            <div className="text-xs text-gray-500 dark:text-gray-400 pt-4 border-t dark:border-gray-700">
                                Created: {selectedBooking.created_at}
                            </div>
                        </div>
                        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0">
                            <button onClick={() => { setIsBookingSliderOpen(false); setSelectedBooking(null); }} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CounsellorsAdmin;